import {
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Switch,
  Typography,
} from "antd";
import React, { useEffect } from "react";
import { TFunction, useTranslation } from "react-i18next";
import {
  DynamicModel,
  GetBeaconConfigurationStateResponse,
  GetSCBOneConfigurationStateResponse,
} from "../../api/models";
import { ApiState } from "../../api/useApiState";
import {
  ContextType,
  useAttributesValidationLogic,
} from "./useAttributesValidationLogic";

interface ConfigFormSingleProps {
  context: ContextType;
  configuration: ApiState<
    | GetBeaconConfigurationStateResponse
    | GetSCBOneConfigurationStateResponse
    | undefined
  >;
  form: FormInstance<any>;
  setFormDataReady: (value: boolean) => void;
  onValuesChange?: (changedValues: any, allValues: any) => void;
}

const { Text } = Typography;

export const ConfigFormSingle = ({
  context,
  configuration,
  form,
  setFormDataReady,
  onValuesChange,
}: ConfigFormSingleProps) => {
  const { t } = useTranslation();

  const attributes = flattenAttributes(configuration.value);
  const attributesValidationLogic = useAttributesValidationLogic();

  useEffect(() => {
    setFormDataReady(!configuration.loading && !configuration.failed);
  }, [configuration.failed, configuration.loading]);

  const validateFormDataReadyForSave = async () => {
    try {
      await form.validateFields();
    } catch (error) {}
    const errors = form.getFieldsError();

    const isFormValid = errors.every(
      (fieldError) => fieldError.errors.length === 0
    );

    return isFormValid;
  };

  return (
    <div
      style={{
        height: "70vh",
      }}
    >
      {configuration.loading ? (
        <Spin className="centered" />
      ) : configuration.failed ? (
        <div>{t("beacon:configure-unavailable")}</div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          onValuesChange={async (changedValues, allValues) => {
            const isFormValid = await validateFormDataReadyForSave();
            setFormDataReady(isFormValid);
            onValuesChange?.(changedValues, allValues);
          }}
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={10}>
              <Text strong>{t("beacon:configure-attributes")}</Text>
            </Col>
            <Col span={7}>
              <Text strong>{t("beacon:configure-desired")}</Text>
            </Col>
            <Col span={7} style={{ textAlign: "center" }}>
              <Text strong>{t("beacon:configure-reported")}</Text>
            </Col>
          </Row>

          {attributes.map(
            (item: Record<string, string | number>, index: number) => {
              const validation = attributesValidationLogic[item.key] || {};
              return (
                <React.Fragment key={item.key || index}>
                  {subSectionHeaders(index, t, context)}

                  <Row gutter={16} key={item.key || index}>
                    <Col span={10}>
                      <Form.Item>
                        <Text>{t(`${validation.translation}`)}</Text>
                      </Form.Item>
                    </Col>

                    <Col span={7}>
                      {validation.type === "inputNumber" ? (
                        <Form.Item
                          name={item.key}
                          initialValue={item.desired}
                          rules={[
                            {
                              required: validation.required,
                              message: t("required"),
                            },
                            {
                              pattern: validation.pattern,
                              message: validation.message,
                            },
                          ]}
                        >
                          <InputNumber />
                        </Form.Item>
                      ) : validation.type === "input" ? (
                        <Form.Item
                          name={item.key}
                          initialValue={item.desired}
                          rules={[
                            {
                              required: validation.required,
                              message: t("required"),
                            },
                            {
                              pattern: validation.pattern,
                              message: validation.message,
                            },
                          ]}
                        >
                          <Input />
                        </Form.Item>
                      ) : validation.type === "switch" ? (
                        <Form.Item
                          name={item.key}
                          initialValue={item.desired}
                          valuePropName="checked"
                          wrapperCol={{
                            span: 24,
                            style: { textAlign: "center" },
                          }}
                        >
                          <Switch
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                            defaultChecked={Boolean(item.desired)}
                          />
                        </Form.Item>
                      ) : validation.type === "select" ? (
                        <Form.Item name={item.key} initialValue={item.desired}>
                          <Select>
                            <Select.Option value={0}>
                              {DynamicModel[0]}
                            </Select.Option>
                            <Select.Option value={2}>
                              {DynamicModel[2]}
                            </Select.Option>
                            <Select.Option value={3}>
                              {DynamicModel[3]}
                            </Select.Option>
                            <Select.Option value={4}>
                              {DynamicModel[4]}
                            </Select.Option>
                            <Select.Option value={11}>
                              {DynamicModel[11]}
                            </Select.Option>
                          </Select>
                        </Form.Item>
                      ) : undefined}
                    </Col>
                    <Col span={7} style={{ textAlign: "center" }}>
                      <Text>
                        {item.reported === null
                          ? t("none")
                          : validation.type === "switch"
                          ? item.reported
                            ? "Enabled"
                            : "Disabled"
                          : item.key === "positioning.dynamicModel"
                          ? DynamicModel[Number(item.reported)]
                          : String(item.reported)}
                      </Text>
                    </Col>
                  </Row>
                </React.Fragment>
              );
            }
          )}
        </Form>
      )}
    </div>
  );
};

const subSectionHeaders = (
  index: number,
  t: TFunction<"translation">,
  context: ContextType
) => {
  const headerStyle = {
    borderBottom: "1px solid #F0F0F0",
    margin: "0 0 24px 0 ",
    flex: "1",
  };
  if (context === "beacon") {
    switch (index) {
      case 0:
        return <h4 style={headerStyle}>{t("beacon:configure-general")}</h4>;
      case 4:
        return (
          <h4 style={headerStyle}>{t("beacon:configure-accelerometer")}</h4>
        );
      case 9:
        return <h4 style={headerStyle}>{t("beacon:configure-rtk")}</h4>;
      case 14:
        return <h4 style={headerStyle}>{t("beacon:configure-positioning")}</h4>;
      default:
        return "";
    }
  } else {
    switch (index) {
      case 0:
        return (
          <h4 style={headerStyle}>
            {t("beacon:configure-continuous-positioning")}
          </h4>
        );
      case 3:
        return <h4 style={headerStyle}>{t("beacon:configure-rtk")}</h4>;
      case 8:
        return <h4 style={headerStyle}>{t("beacon:configure-positioning")}</h4>;

      default:
        return "";
    }
  }
};

const flattenAttributes = (
  data:
    | GetBeaconConfigurationStateResponse
    | GetSCBOneConfigurationStateResponse
    | undefined,
  prefix = ""
) => {
  let result: {
    key: string;
    reported: number | string;
    desired: number | string;
  }[] = [];

  if (!data?.reported) {
    return result;
  }

  Object.entries(data.reported).forEach(([key, reportedValue]) => {
    const desiredValue = (data.desired as Record<string, any>)[key];
    if (key === "protocol") return;
    if (typeof reportedValue === "object" && reportedValue !== null) {
      result = result.concat(
        flattenAttributes(
          {
            reported: reportedValue,
            desired: desiredValue,
          },
          `${prefix}${key}.`
        )
      );
    } else {
      result.push({
        key: `${prefix}${key}`,
        reported: reportedValue,
        desired: desiredValue,
      });
    }
  });
  return result;
};
